// src/components/ui/FileUploadComponent.jsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Grid,
  Card,
  CardMedia,
  CardContent,
  LinearProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Videocam as VideoIcon,
  Description as DocumentIcon,
  Add as AddIcon,
} from '@mui/icons-material';

/**
 * A reusable file upload component that handles multiple file types
 * 
 * @param {Object} props Component props
 * @param {Array} props.value Array of existing files/assets
 * @param {Function} props.onChange Callback when files change
 * @param {Boolean} props.disabled Whether the upload is disabled
 * @param {String} props.accept File type accept string (e.g. "image/*,video/*")
 * @param {Boolean} props.multiple Allow multiple file uploads
 * @param {Function} props.onRemove Optional callback for removing files
 * @param {Boolean} props.showPreview Show preview of uploaded files
 * @param {String} props.previewSize Size of the preview ('small', 'medium', 'large')
 * @param {Boolean} props.isLoading Whether files are currently uploading
 * @param {Function} props.onUpload Optional callback for handling the upload
 */
const FileUploadComponent = ({
  value = [],
  onChange,
  disabled = false,
  accept = "image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  multiple = true,
  onRemove,
  showPreview = true,
  previewSize = 'medium',
  isLoading = false,
  onUpload,
  buttonLabel = "Upload Files",
  helperText = "Drag and drop files here or click to browse",
}) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  
  // Handle click to open file browser
  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Handle file selection
  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const fileArray = Array.from(files);
    
    // Create file preview URLs
    const newFiles = fileArray.map(file => ({
      file,
      type: file.type.split('/')[0] || 'document',
      name: file.name,
      size: file.size,
      url: URL.createObjectURL(file),
    }));
    
    // Call onChange with the new files
    if (onChange) {
      onChange(multiple ? [...value, ...newFiles] : newFiles);
    }
    
    // Call onUpload if provided
    if (onUpload) {
      onUpload(fileArray);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  // Handle drop event
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const fileArray = Array.from(e.dataTransfer.files);
      
      // Create file preview URLs
      const newFiles = fileArray.map(file => ({
        file,
        type: file.type.split('/')[0] || 'document',
        name: file.name,
        size: file.size,
        url: URL.createObjectURL(file),
      }));
      
      // Call onChange with the new files
      if (onChange) {
        onChange(multiple ? [...value, ...newFiles] : newFiles);
      }
      
      // Call onUpload if provided
      if (onUpload) {
        onUpload(fileArray);
      }
    }
  };
  
  // Handle remove file
  const handleRemoveFile = (file, index) => {
    // Revoke object URL to prevent memory leaks
    if (file.url && file.url.startsWith('blob:')) {
      URL.revokeObjectURL(file.url);
    }
    
    // Remove the file from the array
    const newFiles = [...value];
    newFiles.splice(index, 1);
    
    // Call onChange with the updated array
    if (onChange) {
      onChange(newFiles);
    }
    
    // Call onRemove if provided
    if (onRemove) {
      onRemove(file, index);
    }
  };
  
  // Get icon for file type
  const getFileTypeIcon = (fileType) => {
    switch (fileType) {
      case 'image':
        return <ImageIcon />;
      case 'video':
        return <VideoIcon />;
      default:
        return <DocumentIcon />;
    }
  };
  
  // Get appropriate height for preview based on size
  const getPreviewHeight = () => {
    switch (previewSize) {
      case 'small': return 100;
      case 'large': return 200;
      default: return 140;
    }
  };
  
  return (
    <Box>
      {/* File input for browser */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        accept={accept}
        multiple={multiple}
        disabled={disabled}
      />
      
      {/* Upload area */}
      <Box
        sx={{
          border: '2px dashed',
          borderColor: dragActive ? 'primary.main' : 'divider',
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          backgroundColor: dragActive ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
          cursor: disabled ? 'default' : 'pointer',
          transition: 'all 0.2s ease',
          mb: 2,
        }}
        onClick={!disabled ? handleButtonClick : undefined}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <UploadIcon 
          sx={{ 
            fontSize: 40, 
            color: dragActive ? 'primary.main' : 'text.secondary',
            mb: 1,
          }} 
        />
        
        <Typography variant="h6" gutterBottom>
          {buttonLabel}
        </Typography>
        
        <Typography variant="body2" color="textSecondary">
          {helperText}
        </Typography>
        
        {isLoading && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress />
          </Box>
        )}
      </Box>
      
      {/* File previews */}
      {showPreview && value.length > 0 && (
        <Grid container spacing={2}>
          {value.map((file, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card>
                {file.type === 'image' ? (
                  <CardMedia
                    component="img"
                    height={getPreviewHeight()}
                    image={file.url}
                    alt={file.name}
                  />
                ) : (
                  <Box
                    sx={{
                      height: getPreviewHeight(),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'action.hover',
                    }}
                  >
                    {getFileTypeIcon(file.type)}
                  </Box>
                )}
                
                <CardContent sx={{ py: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: '80%' }}>
                      {file.name || file.originalname}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(file, index);
                      }}
                      disabled={disabled}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default FileUploadComponent;